import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils/index';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppDispatch } from '../../store';

// PACKAGES
import * as Yup from 'yup';
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

//COMPONENTS
import Button from '../../components/Button';
import DateInput from '../../components/DateInput';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { StaffReferenceCommand } from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface AddDocumentModalTypes {
	closeModal?: closeModalType;
	formType: string;
	staffId?: string;
	currentFormID?: string;
	getStaffProfileData: () => Promise<void>;
}

const AddDocumentModal: React.FC<AddDocumentModalTypes> = ({
	closeModal,
	formType,
	staffId,
	getStaffProfileData,
	currentFormID
}) => {
	const dispatch = useAppDispatch();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	//Set read only if form has been filled out and submitted already to prevent changes
	const [readOnly, setReadOnly] = React.useState<boolean>(false);

	// Set up templates for Reference Form and Police Check Form
	const referenceCheckTemplate: StaffReferenceCommand = {
		id: undefined,
		userProfileId: undefined,
		checked: undefined,
		name: undefined,
		position: undefined,
		organisation: undefined,
		contactDetails: undefined,
		verifiedBy: undefined
	};

	// State for radio button
	const [referenceIsChecked, setReferenceIsChecked] = React.useState<boolean>(false);

	//Initial form State
	const initialFormStateReferenceCheck = [{ ...referenceCheckTemplate }];

	// Set up form data state for both Reference Check and Police Check
	const [formData, setFormData] = React.useState({
		refChecks: initialFormStateReferenceCheck,
		isReferenceChecked: referenceIsChecked
	});

	// If there is an Id, then call api and load either Reference Check form or Police Check form based on formType
	// If there is No Id, then load initial blank form of either Reference Check form or Police Check form based on formType
	const getModalDetail = async () => {
		setIsLoading(true);

		if (currentFormID) {
			if (formType === 'Reference checks') {
				const response = await apibridge.getStaffReference(currentFormID);

				if (response.isError) {
					setIsLoading(false);

					setFormErrorMessage(
						response.validationErrors.length
							? validationErrorsToString(response.validationErrors)
							: response.message || 'An error has occurred'
					);
				} else {
					setIsLoading(false);

					setFormData({
						refChecks: [response.result],

						isReferenceChecked: response.result.checked ? true : referenceIsChecked
					});

					setReferenceIsChecked(true);

					setReadOnly(true);
				}
			}
		} else {
			setFormData({
				refChecks: initialFormStateReferenceCheck,

				isReferenceChecked: referenceIsChecked
			});
		}
		setIsLoading(false);
	};

	React.useEffect(() => {
		getModalDetail();
	}, []);

	// Validation
	const referenceChecksValidationSchema = Yup.object({
		refChecks: Yup.array().when('isReferenceChecked', {
			is: true,
			then: Yup.array().of(
				Yup.object({
					checked: Yup.string().required('Required'),
					name: Yup.string().required('Required'),
					position: Yup.string().required('Required'),
					organisation: Yup.string().required('Required'),
					contactDetails: Yup.string().required('Required')
				})
			)
		})
	});

	// Submit form data to API
	const submitFormData = async (values: any) => {
		setIsLoading(true);

		if (formType === 'Reference checks') {
			if (values.isReferenceChecked) {
				values.refChecks.map(async (ref: StaffReferenceCommand) => {
					const data: StaffReferenceCommand = {
						id: ref.id,
						userProfileId: staffId,
						checked: ref.checked,
						name: ref.name,
						position: ref.position,
						organisation: ref.organisation,
						contactDetails: ref.contactDetails
					};

					const response = await apibridge.postStaffReference(data);

					if (response.isError) {
						setIsLoading(false);

						setFormErrorMessage(
							response.validationErrors.length
								? validationErrorsToString(response.validationErrors)
								: response.message || 'An error has occurred'
						);
					} else {
						setIsLoading(false);

						if (closeModal) {
							closeModal();
						}

						getStaffProfileData();

						useUpdateTotalNotification();
					}
				});
			} else {
				setIsLoading(false);

				if (closeModal) {
					closeModal();
				}
			}
		}

		if (formType === 'Police check') {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}
		}
	};

	return (
		<div className="document-form-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik
								enableReinitialize
								initialValues={formData}
								validationSchema={referenceChecksValidationSchema}
								onSubmit={submitFormData}
							>
								{(formik) => {
									return (
										<Form translate="yes">
											{formType === 'Reference checks' ? (
												<>
													<div className="form-group">
														<label htmlFor="referenceCheckComplete">
															Have at least two reference checks been undertaken prior to this person’s employment?
														</label>

														<label className="form-control-radio">
															{' '}
															<Field
																readOnly={readOnly}
																disabled={currentFormID}
																name="isReferenceChecked"
																type="radio"
																checked={referenceIsChecked === false}
																onChange={() => {
																	setReferenceIsChecked(false), formik.setFieldValue('isReferenceChecked', false);
																}}
															/>
															No
														</label>

														<label className="form-control-radio">
															{' '}
															<Field
																readOnly={readOnly}
																name="isReferenceChecked"
																type="radio"
																checked={referenceIsChecked === true}
																onChange={() => {
																	setReferenceIsChecked(true), formik.setFieldValue('isReferenceChecked', true);
																}}
															/>
															Yes
														</label>
													</div>

													<div className="general-divider"></div>

													{referenceIsChecked && (
														<FieldArray name="refChecks">
															{(props) => {
																const { push, remove, form } = props;
																const { values } = form;
																const { refChecks } = values;

																return (
																	<>
																		{refChecks.map((ref: any, index: number) => (
																			<div key={index}>
																				{ref.verifiedBy && (
																					<div className="form-group">
																						<label>Verified by:</label>
																						<Field
																							className="form-control disabled-input"
																							type="text"
																							value={ref.verifiedBy}
																							readOnly
																						/>
																					</div>
																				)}
																				<div className="form-group">
																					<label htmlFor="checked">Date of check</label>

																					<Field
																						name={`refChecks[${index}].checked`}
																						children={() => (
																							<DateInput
																								dateTimeString={ref.checked}
																								onChange={(val: any) =>
																									formik.setFieldValue(`refChecks[${index}].checked`, val)
																								}
																							/>
																						)}
																					/>

																					<ErrorMessage
																						component={'div'}
																						name={`refChecks[${index}].checked`}
																						className="error-message"
																					/>
																				</div>

																				<div className="form-group">
																					<label htmlFor="name">Name of reference</label>

																					<Field
																						name={`refChecks[${index}].name`}
																						className="form-control"
																						type="text"
																					/>
																					<ErrorMessage
																						component={'div'}
																						name={`refChecks[${index}].name`}
																						className="error-message"
																					/>
																				</div>

																				<div className="form-group">
																					<label htmlFor="position">Position</label>

																					<Field
																						name={`refChecks[${index}].position`}
																						className="form-control"
																						type="text"
																					/>
																					<ErrorMessage
																						component={'div'}
																						name={`refChecks[${index}].position`}
																						className="error-message"
																					/>
																				</div>

																				<div className="form-group">
																					<label htmlFor="organisation"> Organisation </label>

																					<Field
																						name={`refChecks[${index}].organisation`}
																						className="form-control"
																						type="text"
																					/>
																					<ErrorMessage
																						component={'div'}
																						name={`refChecks[${index}].organisation`}
																						className="error-message"
																					/>
																				</div>

																				<div className="form-group">
																					<label htmlFor="">Contact details</label>

																					<Field
																						name={`refChecks[${index}].contactDetails`}
																						className="form-control"
																						placeholder="Please provide either mobile or email address"
																						type="text"
																					/>
																					<ErrorMessage
																						component={'div'}
																						name={`refChecks[${index}].contactDetails`}
																						className="error-message"
																					/>
																				</div>

																				<div className="form-group">
																					{index !== 0 ? (
																						<Button onClick={() => remove(index)} type="button" className="btn-text">
																							Remove
																						</Button>
																					) : null}
																				</div>

																				<div className="general-divider"></div>
																				{formik.values.refChecks.length === index + 1 ? (
																					<div className="form-group">
																						<Button
																							type="button"
																							className="btn-secondary full-width"
																							onClick={() => push({ ...referenceCheckTemplate })}
																						>
																							<PlusIcon /> Add another
																						</Button>
																					</div>
																				) : null}
																			</div>
																		))}
																	</>
																);
															}}
														</FieldArray>
													)}
												</>
											) : null}

											<Button type="submit" className="btn-primary full-width">
												Save and Close
											</Button>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default AddDocumentModal;
